import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message, Dropdown } from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CaretRightOutlined,
  DownOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getCampaigns,
  deleteCampaign,
  launchCampaign,
  pauseCampaign,
  resumeCampaign,
  type MarketingCampaign,
  type CampaignFilters,
} from '@/api/marketingApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  active: 'green',
  paused: 'orange',
  completed: 'purple',
  cancelled: 'red',
};

const TYPE_COLORS: Record<string, string> = {
  email: 'cyan',
  sms: 'geekblue',
  social: 'magenta',
};

export default function CampaignListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<CampaignFilters>({});

  const canWrite = hasPermission('campaigns', 'write');
  const canDelete = hasPermission('campaigns', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof CampaignFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getCampaigns({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('campaigns.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteCampaign(id);
            message.success(t('campaigns.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleLifecycle = useCallback(
    async (action: 'launch' | 'pause' | 'resume', id: string) => {
      try {
        if (action === 'launch') await launchCampaign(id);
        else if (action === 'pause') await pauseCampaign(id);
        else await resumeCampaign(id);
        message.success(t(`campaigns.${action}_success`));
        gridRef.current?.reload();
      } catch {
        message.error(t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<MarketingCampaign> = useMemo(
    () => [
      {
        title: t('campaigns.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        ellipsis: true,
        render: (name: string, record: MarketingCampaign) => (
          <a onClick={() => navigate(`/marketing/campaigns/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('campaigns.field.type'),
        dataIndex: 'type',
        key: 'type',
        width: 110,
        render: (type: string) => (
          <Tag color={TYPE_COLORS[type] ?? 'default'}>
            {t(`campaigns.type_options.${type}`, type)}
          </Tag>
        ),
      },
      {
        title: t('campaigns.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`campaigns.status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('campaigns.field.owner_id'),
        dataIndex: 'owner_id',
        key: 'owner_id',
        ellipsis: true,
      },
      {
        title: t('campaigns.field.total_recipients'),
        key: 'total_recipients',
        width: 100,
        render: (_: unknown, record: MarketingCampaign) =>
          record.tracking?.total_recipients ?? 0,
      },
      {
        title: t('campaigns.field.sent_count'),
        key: 'sent_count',
        width: 80,
        render: (_: unknown, record: MarketingCampaign) =>
          record.tracking?.sent_count ?? 0,
      },
      {
        title: t('campaigns.field.opened_count'),
        key: 'opened_count',
        width: 80,
        render: (_: unknown, record: MarketingCampaign) =>
          record.tracking?.opened_count ?? 0,
      },
      {
        title: t('campaigns.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 180,
        render: (_: unknown, record: MarketingCampaign) => {
          const lifecycleItems = [];
          if ((record.status === 'draft' || record.status === 'scheduled') && canWrite) {
            lifecycleItems.push({
              key: 'launch',
              icon: <PlayCircleOutlined />,
              label: t('campaigns.action.launch'),
              onClick: () => handleLifecycle('launch', record.id),
            });
          }
          if (record.status === 'active' && canWrite) {
            lifecycleItems.push({
              key: 'pause',
              icon: <PauseCircleOutlined />,
              label: t('campaigns.action.pause'),
              onClick: () => handleLifecycle('pause', record.id),
            });
          }
          if (record.status === 'paused' && canWrite) {
            lifecycleItems.push({
              key: 'resume',
              icon: <CaretRightOutlined />,
              label: t('campaigns.action.resume'),
              onClick: () => handleLifecycle('resume', record.id),
            });
          }

          return (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/marketing/campaigns/${record.id}`)}
              />
              {canWrite && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/marketing/campaigns/${record.id}/edit`)}
                />
              )}
              {canDelete && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              )}
              {lifecycleItems.length > 0 && (
                <Dropdown menu={{ items: lifecycleItems }}>
                  <Button type="link" size="small" icon={<DownOutlined />} />
                </Dropdown>
              )}
            </Space>
          );
        },
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, handleLifecycle]
  );

  const typeOptions = useMemo(
    () => [
      { value: '', label: t('campaigns.filter.all') },
      { value: 'email', label: t('campaigns.type_options.email') },
      { value: 'sms', label: t('campaigns.type_options.sms') },
      { value: 'social', label: t('campaigns.type_options.social') },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('campaigns.filter.all') },
      { value: 'draft', label: t('campaigns.status_options.draft') },
      { value: 'scheduled', label: t('campaigns.status_options.scheduled') },
      { value: 'active', label: t('campaigns.status_options.active') },
      { value: 'paused', label: t('campaigns.status_options.paused') },
      { value: 'completed', label: t('campaigns.status_options.completed') },
      { value: 'cancelled', label: t('campaigns.status_options.cancelled') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('campaigns.filter.type')}
        style={{ width: 140 }}
        allowClear
        options={typeOptions}
        onChange={(val) => handleFilterChange('type', val || undefined)}
      />
      <Select
        placeholder={t('campaigns.filter.status')}
        style={{ width: 140 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      <Button
        icon={<BarChartOutlined />}
        onClick={() => navigate('/marketing/analytics')}
      >
        {t('campaigns.analytics.title')}
      </Button>
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/marketing/campaigns/new')}
        >
          {t('campaigns.new_campaign')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('campaigns.list_title')}</h2>
      <DataGrid<MarketingCampaign>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('campaigns.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/marketing/campaigns/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
